import { Block } from 'baseui/block';
import {
    DisplayXSmall,
    LabelSmall,
    LabelLarge,
    LabelXSmall,
    ParagraphXSmall,
    ParagraphSmall
} from 'baseui/typography';
import { useIntl, IntlShape } from 'react-intl';
import { defaultCurrencyCode, getCurrencySymbol } from '../../../utils/currency';
import getSymbolFromCurrency from 'currency-symbol-map';

export type ValueDisplayProps = {
    value: number | boolean | string,
    dimension?: string,
    small?: boolean,
    icon?: any,
    color?: string,
    mini?: boolean,
    style?: NumberFormatOptionsStyle | undefined,
    currencyCode?: string
}

export type NumberFormatOptionsStyle = 'decimal' | 'percent' | 'currency' | 'unit';

const ValueDisplay = ({ value, dimension, small, icon, color, mini, style, currencyCode }: ValueDisplayProps) => {

    const intl = useIntl();
    const valueTypeString = (typeof value === 'string');//|| dimension === undefined) && typeof value !== 'boolean';
    const isCurrency = style === 'currency';
    const dim = isCurrency ? `${getSymbolFromCurrency(currencyCode ?? 'EUR')}${dimension}` : dimension;
    const val = valueTypeString ? value : (isCurrency ? getCurrencyValue(Number(value), intl, currencyCode ?? defaultCurrencyCode) : getValue(value, dimension ?? '', intl, style));

    if (mini)
        return <MiniValueDisplay icon={icon} color={color} val={val} dim={dim}></MiniValueDisplay>

    if (small)
        return <SmallValueDisplay icon={icon} color={color} val={val} dim={dim}></SmallValueDisplay>

    return <NormalValueDisplay icon={icon} color={color} val={val} dim={dim}></NormalValueDisplay>
}
export default ValueDisplay;


const SmallValueDisplay = ({ icon, color, val, dim }) => {
    return (<Block display='flex' flexDirection='row' alignItems='baseline' gridGap='2px'>
        {icon}
        <LabelLarge color={color}>{val}</LabelLarge>
        <ParagraphSmall margin={0}>{dim}</ParagraphSmall>
    </Block>)
}

const MiniValueDisplay = ({ icon, color, val, dim }) => {
    return (<Block display='flex' flexDirection='row' alignItems='baseline' gridGap='2px'>
        {icon}
        <LabelSmall margin={0} color={color}>{val}</LabelSmall>
        <ParagraphXSmall  margin={0}>{dim}</ParagraphXSmall>
    </Block>)
}

const NormalValueDisplay = ({ icon, color, val, dim }) =>
    <Block display='flex' flexDirection='row' alignItems='baseline' gridGap='2px'>
        {icon}
        <DisplayXSmall margin={0} color={color}>{val}</DisplayXSmall>
        <LabelSmall>{dim}</LabelSmall>
    </Block>


interface IDictionary {
    [index: string]: any;
}
const decimalPoints = {
    '°C': 1,
    '°F': 1,
    '%rF': 0,
    '%': 0,
    'Pa': 0,
    'pa': 0,
    'bar': 2,
    'kg': 0,
    'kg/m²': 2,
    '€': 0,
    '€/m²': 2,
    'MWh': 1,
    'MWh/m²': 2,
    'kWh': 0,
    'kWh/m²': 1,
    'm³': 1,
    'K': 1,
    'lx': 0,
    'lux': 0,
    'km/h': 1,
    'V': 1,
    'A': 1,
    'h': 0,
    'default': 2,
} as IDictionary


function getValue(value: number | boolean, dimension: string, intl: IntlShape, style: NumberFormatOptionsStyle | undefined) {
    if (typeof value === 'boolean') {
        return Number(value) > 0 ? intl.formatMessage({ id: 'on', defaultMessage: 'ON' }).toUpperCase() : intl.formatMessage({ id: 'off', defaultMessage: 'OFF' }).toUpperCase();
    }
    const decimals = decimalPoints[dimension] ?? decimalPoints['default'];
    return intl.formatNumber(value ? parseFloat(value.toFixed(decimals)) : getDecimalsBaseOnValue(value), { style: style });
}


const getCurrencyValue = (value: number, intl: IntlShape, currencyCode: string) => {
    return intl.formatNumber(value, { minimumFractionDigits: getDecimalsBaseOnValue(value), maximumFractionDigits: getDecimalsBaseOnValue(value) });
}

const getDecimalsBaseOnValue = (value: number) => {
    if (value === 0)
        return 0;
    if (value > 100)
        return 0;
    if (value > 5)
        return 1;
    else
        return 2;
}